/**
 * recent_searches.js
 * ------------------
 * CL-22: the member's recent searches, kept in this browser's localStorage and
 * shown in the search overlay before they type.
 *
 * What is stored is only the search text, its kind, and (for a store the member
 * picked from the results) the store id. Links are rebuilt from those when
 * rendering (recentHref), never read back from storage, so a tampered entry
 * cannot become an off-site link. Searches longer than MAX_TEXT_LENGTH are not
 * saved, because a shortened copy would run a different search.
 *
 * History is scoped to whoever is signed in: the header passes an opaque scope
 * (a hash of the user, or "anon"). If the stored scope differs from the current
 * one the history is discarded, so one person's searches never show for
 * another on a shared computer.
 *
 * Every storage call is wrapped: private browsing, blocked site data or a full
 * quota just mean searches are not remembered.
 */

export const STORAGE_KEY = "shop:recent_searches:v1";
export const MAX_ITEMS = 5;
export const MIN_TEXT_LENGTH = 2;
export const MAX_TEXT_LENGTH = 80;

// Collapses runs of whitespace. That does not change what a search finds, so a
// search saved this way still matches what the member typed.
function normalizeText(text) {
  if (typeof text !== "string") return "";
  return text.replace(/\s+/g, " ").trim();
}

// Returns a clean {kind, text[, id]} or null. A store entry without a usable
// id is kept as a plain search for its name rather than trusted as a link.
export function sanitizeEntry(entry) {
  if (!entry || typeof entry !== "object") return null;
  const text = normalizeText(entry.text);
  if (text.length < MIN_TEXT_LENGTH) return null;
  if (
    entry.kind === "store" &&
    Number.isSafeInteger(entry.id) &&
    entry.id > 0
  ) {
    // The link comes from the id, so a very long name only needs a shorter label.
    return {
      kind: "store",
      text: text.slice(0, MAX_TEXT_LENGTH).trim(),
      id: entry.id,
    };
  }
  // A search is replayed from its text, so it is saved whole or not at all: a
  // cut-short copy would run a different search than the one the member made.
  if (text.length > MAX_TEXT_LENGTH) return null;
  return { kind: "query", text };
}

// Built from the clean entry only. A store link relies on /stores/:id
// redirecting to the canonical /stores/:id-:slug.
export function recentHref(entry) {
  if (entry.kind === "store") return `/stores/${entry.id}`;
  return `/offers?q=${encodeURIComponent(entry.text)}`;
}

function defaultStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function createRecentSearches({
  scope,
  storage = defaultStorage(),
  max = MAX_ITEMS,
} = {}) {
  function read() {
    if (!storage) return [];
    try {
      const raw = storage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const data = JSON.parse(raw);
      if (!data || typeof data !== "object" || data.scope !== scope) {
        // Someone else's history (or the signed-in user changed): drop it.
        storage.removeItem(STORAGE_KEY);
        return [];
      }
      if (!Array.isArray(data.items)) return [];
      const seen = new Set();
      return data.items
        .map(sanitizeEntry)
        .filter((entry) => {
          if (!entry) return false;
          const key = entry.text.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .slice(0, max);
    } catch {
      return [];
    }
  }

  function write(items) {
    if (!storage) return;
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify({ scope, items }));
    } catch {
      // Storage full or blocked: searches just are not remembered.
    }
  }

  return {
    list: read,
    // Newest first, one entry per text (case ignored), at most `max`.
    add(entry) {
      const clean = sanitizeEntry(entry);
      if (!clean) return;
      const key = clean.text.toLowerCase();
      const rest = read().filter((e) => e.text.toLowerCase() !== key);
      write([clean, ...rest].slice(0, max));
    },
    clear() {
      if (!storage) return;
      try {
        storage.removeItem(STORAGE_KEY);
      } catch {
        // Nothing to do.
      }
    },
  };
}
